import { useState } from 'react';

import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogLoader } from '../../../../components/Dialog/DialogLoader';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../../components/FormInput/FormInput';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';
import { emailValidator } from '../../../../utils/validators';
import { getEmailErrorMessageMap } from '../../SignUpRegister/constants';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { HeaderLogoElement } from '../HeaderLeftElement/HeaderLogoElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';

import styles from './ResetPasswordRedesign.module.scss';

interface ResetPasswordRedesignProps extends WizardChildProps {
  /** Overrides the default "go back to the log-in wizard" navigation (used by the /native flow). */
  onGoBack?: () => void;
  /** Overrides the default "open the sign-up wizard" navigation (used by the /native flow). */
  onOpenSignUp?: () => void;
}

export const ResetPasswordRedesign = observer(function ResetPasswordRedesign(props: ResetPasswordRedesignProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');

  const { t } = useTranslation();

  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const isSocialLanding = authStore.isSocialLanding();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const isProcessing = computed(() => authStore.pending('sendPasswordResetLink')).get();

  const handleSubmit = async () => {
    try {
      const emailError = email.trim() ? emailValidator(email.trim()) || '' : t('auth.pleaseFillInAllRequiredFields');
      if (emailError) {
        setError(emailError);
        return;
      }

      setError('');

      void analytics.sendEvent('reset_password', {
        event_action: 'click',
        button_text: 'reset password',
        event_category: 'sign_up',
        event_value: 'enter_email_address',
        event_type: 'reset_password',
        status: 'success',
      });

      await authStore.sendPasswordResetLink(email);
      props.nextStep();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleOpenSignUp = () => {
    if (props.onOpenSignUp) {
      props.onOpenSignUp();
      return;
    }

    openAuthWizard('signUpWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: false,
      landingKey: landingKey ?? undefined,
    });
  };

  const handleGoBack = () => {
    if (props.onGoBack) {
      props.onGoBack();
      return;
    }

    if (isSocialLanding) {
      portalStore.closePortal();
      return;
    }

    openAuthWizard('logInWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: false,
      landingKey: landingKey ?? undefined,
    });
  };

  const onChangeEmail = (newEmail: string) => {
    setError('');
    setEmail(newEmail);
  };

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="reset-password-close-btn"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="reset-password-back-btn"
        headerLeftElement={<HeaderLogoElement showLogo={isDesktop} />}
        title={t('auth.resetPassword')}
        titleClassName={styles.dialogHeaderTitle}
        {...(isDesktop ? { onClose: handleGoBack } : { onGoBack: handleGoBack })}
      />
      <DialogLoader isProcessing={isProcessing} />
      <div className={styles.containerContent}>
        <div className={styles.rowLineContainer}>
          <span className={styles.rowLineText}>{t('auth.enterEmailInstructions')}</span>
        </div>

        <div className={styles.emailInputWrapper}>
          <FormInput
            hideErrors
            required
            className={classNames({ [styles.errorStyleInput]: !!error })}
            data-testid="reset-password-email-field"
            disabled={isProcessing}
            errorMessageMap={getEmailErrorMessageMap(t)}
            id="email"
            name="email"
            placeholder={t('auth.enterEmail')}
            size={'md'}
            type="email"
            onChange={(event) => onChangeEmail(event.target.value)}
          />
        </div>

        <ErrorMessage className={styles.authErrorMessage}>{error}</ErrorMessage>

        <div className={styles.resetPasswordButtonWrapper}>
          <Button
            btnType="main"
            className={styles.resetPasswordButton}
            dataTestId="reset-password-btn"
            onClick={handleSubmit}
          >
            {t('auth.resetPassword')}
          </Button>
        </div>

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
          data-testid="reset-password-redesign-signup-line"
        >
          <Trans
            components={{
              question: <span />,
              signup: <span className={styles.signUpLink} data-testid="lobby-signup-btn" onClick={handleOpenSignUp} />,
            }}
            i18nKey="auth.noAccountYetSignUp"
          />
        </div>
      </div>
    </RedesignContainer>
  );
});
